import Refinements from "reducers/Refinements";
import { isEqual } from "lodash";

export const TWO = 2;

/**
 * Utility function to translate frame position and offsets from FT to EasyCrop format
 *
 * @param {
 *	left - left offset of the frame
 *  top - top offset of the frame
 *  frameWidth - width of the frame
 *  frameHeight - height of the frame
 *  canvasWidth - width of the canvas
 *  canvasHeight - height of the canvas
 *  assetWidth -  width of the asset
 * } Object defining offsets and dimensions in FT format
 * @returns Object with x, y, and zoom attributes in EasyCrop format
 */
export const translateOffsetsToEasyCrop = ({
	left,
	top,
	frameWidth,
	frameHeight,
	canvasWidth,
	canvasHeight,
	assetWidth,
}) => {
	const zoom = canvasWidth / assetWidth
		, x = canvasWidth / TWO - left - frameWidth / TWO
		, y = canvasHeight / TWO - top - frameHeight / TWO;
	return { x, y, zoom };
};

/**
 * Utility function to translate frame position and offsets from EasyCrop to FT format
 *
 * @param {
 * x - x offset from center of the frame,
 * y - y offset from center of the frame,
 * zoom - zoom level,
 * frameWidth - width of the frame,
 * frameHeight - height of the frame,
 * assetWidth - asset width,
 * assetHeight - asset height
 * } Object defining offsets and dimensions in EasyCrop format
 * @returns Object with left and top attributes in FT format
 */
export const translateOffsetsFromEasyCrop = ({
	x,
	y,
	zoom,
	frameWidth,
	frameHeight,
	assetWidth,
	assetHeight }) => {
	const left = assetWidth * zoom / TWO - x - frameWidth / TWO
		, top = assetHeight * zoom / TWO - y - frameHeight / TWO;
	return { left, top };
};

/**
 *
 * Function to calculate if canvas is modified - is taking into consideration rounding of the values
 * @param canvas current canvas
 * @param initialCanvas
 * @returns boolean - true if canvas is modified, false otherwise.
 */
export const isCanvasModified = (canvas, initialCanvas) => {
	let roundedCanvas = canvas;
	if (roundedCanvas && roundedCanvas.width) {
		roundedCanvas = {
			left: Math.round(roundedCanvas.left),
			top: Math.round(roundedCanvas.top),
			width: Math.round(roundedCanvas.width),
			height: Math.round(roundedCanvas.height),
		};
	}

	return !isEqual(roundedCanvas, initialCanvas);
};


/**
 * This Utility function calculates if at least one Image variable has been adjusted
 * and controls enabled status of 'Apply Updates' button.
 * @param values - object defining the current values of the form
 * @param initialValues - object defining the initial values of the form
 * @returns boolean - true if at least one image variable has been adjusted, false otherwise
 */
export const isFrameAdjusted = (values, initialValues) => {
	for (const key in values) {
		// We are only interested in image variaples (Untill we handle video cropping as well)
		if (values[key].type !== "Image") {
			continue;
		}

		if (isCanvasModified(values[key].cropMetadata?.canvas, initialValues[key].cropMetadata?.canvas)) {
			return true;
		}
	}
	return false;
};

/**
 * This Utility function gets content of inner iframe from the endpoint and refines the preview
 * content by replacing all extermal src and href URLs (avoiding cross origin issues) with the
 * to appropriatre base urls. Also substitute images with dataurls based on the refinedDataMap.
 * returns content that is set to an iframe using srcDoc attribute. Which creates 100% match of preview
 * of the unsaved data.
 * @param {
 * previewContent - content of the inner iframe that is comming from Ajax call,
 * refinedDataMap - Map of variable names and their respective refined data URLs (as we could have up to 5 refined images),
 * iframeBaseUrl - base URL of the iframe - this will prepend all relative URLs in the preview content,
 * routeFile - name of the route file,
 * allRefinementsLoaded - flag indicating that data urls for all images are ready
 * } Object containing metadata for preview
 * @returns content that is set to an iframe using srcDoc attribute. Which creates 100% match of preview
 * of the unsaved data
 */
export const getRefinedPreviewContent = ({
	previewContent,
	refinedDataMap,
	iframeBaseUrl,
	routeFile,
	allRefinementsLoaded,
}) => {
	if (allRefinementsLoaded && previewContent) {
		let updatedPreviewContent = `${previewContent}`;
		if (routeFile === "index.html") {
			// Add base URL to all relative URLs
			updatedPreviewContent = updatedPreviewContent.replace(/src="(?!\/\/)/g, `src="${iframeBaseUrl}/`);
			updatedPreviewContent = updatedPreviewContent.replace(/src="\/\/(?!http)/g, 'src="https://');
			updatedPreviewContent = updatedPreviewContent.replace(/href="/g, `href="${iframeBaseUrl}/`);

			//Replace all keys in the preview content with the corresponding refined content data urls
			//and texts if updates are available
			for (const [key, value] of refinedDataMap) {
				if (value.text) {
					updatedPreviewContent = updatedPreviewContent.replace(`<ft-dynamic name="${key}">`, `<ft-dynamic name="${key}">${value.text}`);
				} else if (value.refinementDataUrl) {
					updatedPreviewContent = updatedPreviewContent.replace(`<ft-dynamic name="${key}">`, `<ft-dynamic name="${key}"><img src="${value.refinementDataUrl}" />`);
				}
			}
		} else if (routeFile === "main.html") {
			//for (const [key, value] of refinedDataMap) {
			// TODO: Handle main.html
			//}
		}

		return updatedPreviewContent;
	}
};

export default Refinements;
